import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/contexts/SharedAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateTicket } from '@/hooks/useTickets';
import { calculatePriority, FORM_ASSIGNMENTS } from '../../../../../shared/form-config';
import { useTranslation } from 'react-i18next';
import { ReporterInfoFields } from '@/components/tickets/ReporterInfoFields';
import { TicketFormButtons } from '@/components/tickets/TicketFormButtons';

const userOnboardingSchema = z.object({
  summary: z.string().min(5, 'El resumen debe tener al menos 5 caracteres'),
  firstName: z.string().min(1, 'El nombre es requerido'),
  lastName: z.string().min(1, 'El apellido es requerido'),
  clapbase: z.string().min(1, 'Debe seleccionar una ubicación'),
  area: z.string().min(1, 'El área es requerida'),
  contractType: z.string().min(1, 'El tipo de contrato es requerido'),
  position: z.string().min(1, 'El cargo es requerido'),
  phone: z.string().min(1, 'El teléfono es requerido'),
  identification: z.string().min(1, 'La identificación es requerida'),
  startDate: z.string().min(1, 'La fecha de inicio es requerida'),
  startTime: z.string().optional(),
  additionalComments: z.string().optional(),
});

type UserOnboardingFormData = z.infer<typeof userOnboardingSchema>;

const CLAPBASE_OPTIONS = [
  'Córdoba',
  'Mendoza',
  'Buenos Aires',
  'Rosario Argentina',
  'Chile',
  'Brasil',
  'Estados Unidos',
  'Colombia',
  'Mexico',
];

export function UserOnboardingForm() {
  const { user } = useAuth();
  const { t } = useTranslation();

  const { register, handleSubmit, formState: { errors }, setValue } = useForm<UserOnboardingFormData>({
    resolver: zodResolver(userOnboardingSchema),
  });

  // ✨ Using tRPC mutation
  const createTicketMutation = useCreateTicket();
  const onSubmit = async (data: UserOnboardingFormData) => {
    const priority = calculatePriority('user-onboarding', data);
    const ticketData = {
      type: 'user-onboarding',
      title: `Alta de Usuario: ${data.firstName} ${data.lastName}`,
      formData: {
        reporterName: user?.name,
        reporterEmail: user?.email,
        ...data,
      },
      attachments: [],
      priority,
      assignedTo: FORM_ASSIGNMENTS['user-onboarding'],
    };
    createTicketMutation.mutate(ticketData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('tickets.forms.user-onboarding.name')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <ReporterInfoFields name={user?.name} email={user?.email} />

          <div className="space-y-2">
            <Label htmlFor="summary">
              {t('forms.summary')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="summary"
              placeholder={t('tickets.useronboardingform.ej_ingreso_de_nuevo_developer_al')}
              {...register('summary')}
            />
            {errors.summary && (
              <p className="text-sm text-destructive">{errors.summary.message}</p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input id="firstName" {...register('firstName')} />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">
                Apellido <span className="text-destructive">*</span>
              </Label>
              <Input id="lastName" {...register('lastName')} />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clapbase">
              {t('tickets.useronboardingform.clapbase_ubicacion')} <span className="text-destructive">*</span>
            </Label>
            <Select onValueChange={(value) => setValue('clapbase', value)}>
              <SelectTrigger>
                <SelectValue placeholder={t('tickets.useronboardingform.selecciona_ubicacion')} />
              </SelectTrigger>
              <SelectContent>
                {CLAPBASE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.clapbase && (
              <p className="text-sm text-destructive">{errors.clapbase.message}</p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="area">
                {t('tickets.useronboardingform.area')} <span className="text-destructive">*</span>
              </Label>
              <Input id="area" placeholder={t('tickets.useronboardingform.ej_pml_people_sales')} {...register('area')} />
              {errors.area && (
                <p className="text-sm text-destructive">{errors.area.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contractType">
                {t('tickets.useronboardingform.tipo_de_contrato')} <span className="text-destructive">*</span>
              </Label>
              <Input id="contractType" placeholder={t('tickets.useronboardingform.ej_tiempo_completo_part_time')} {...register('contractType')} />
              {errors.contractType && (
                <p className="text-sm text-destructive">{errors.contractType.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">
              Cargo <span className="text-destructive">*</span>
            </Label>
            <Input id="position" placeholder={t('tickets.useronboardingform.ej_senior_developer_account_manager')} {...register('position')} />
            {errors.position && (
              <p className="text-sm text-destructive">{errors.position.message}</p>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phone">
                {t('forms.phone')} <span className="text-destructive">*</span>
              </Label>
              <Input id="phone" type="tel" {...register('phone')} />
              {errors.phone && (
                <p className="text-sm text-destructive">{errors.phone.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="identification">
                {t('tickets.useronboardingform.identificacion_dni_pasaporte')} <span className="text-destructive">*</span>
              </Label>
              <Input id="identification" {...register('identification')} />
              {errors.identification && (
                <p className="text-sm text-destructive">{errors.identification.message}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="startDate">
                {t('tickets.useronboardingform.fecha_de_inicio')} <span className="text-destructive">*</span>
              </Label>
              <Input id="startDate" type="date" {...register('startDate')} />
              {errors.startDate && (
                <p className="text-sm text-destructive">{errors.startDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime">{t('tickets.useronboardingform.hora_de_inicio_opcional')}</Label>
              <Input id="startTime" type="time" {...register('startTime')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="additionalComments">{t('forms.additionalComments')}</Label>
            <Textarea
              id="additionalComments"
              placeholder={t('forms.additionalCommentsPlaceholder')}
              rows={4}
              {...register('additionalComments')}
            />
          </div>

          <TicketFormButtons isPending={createTicketMutation.isPending} />
        </form>
      </CardContent>
    </Card>
  );
}
